import { GameContext } from '../../../contexts/GameContext';
import { GameStat } from '../../../gql-types';
import {
    MGameStepConfiguration,
    mGameNumberSteps,
} from '../../../configuration/games/m-game/mGameContentConfiguration';
import { ProfileContext } from '../../../contexts/ProfileContext';
import { isEmptyObject } from '../../../util/isEmptyObject';
import { useContext, useEffect, useState } from 'react';
import { useGraphQL } from '../../../hooks/useGraphQL';
import GameWrapper from '../../../components/common/wrappers/GameWrapper';
import Loading from '../../../components/common/Loading';
import MCipher from '../../../components/games/m-game/MCipher';
import MCodeInput from '../../../components/games/m-game/MCodeInput';
import MContinueButton from '../../../components/games/m-game/MContinueButton';
import MDiaryIcon from '../../../components/games/m-game/MDiaryIcon';
import MEnding from './MEnding';
import MTaskText from '../../../components/games/m-game/MTaskText';
import MTaskTitle from '../../../components/games/m-game/MTaskTitle';

const DEFAULT_STATS: GameStat = {
    timeSpent: '0',
    hintsTaken: 0,
    mistakes: 0,
    audioPlayed: false,
    codes: [],
    points: 0,
    reportClicked: false,
    mapClicked: false,
};

function MGame() {
    const { startGame, saveProgress, endGame } = useGraphQL();
    const [currentStepStats, setCurrentStepStats] = useState(DEFAULT_STATS);
    const [loading, setLoading] = useState(false);
    const { availableGame } = useContext(ProfileContext);

    const { lastStep, configuration, currentStep, nextStepAction, stepContent, setStepContent, setCurrentStep } =
        useContext(GameContext);

    const { title, taskText, continueButtonText, codeInputText, codeInputPassCodeRegexp, diaryIcon } =
        stepContent as MGameStepConfiguration;

    useEffect(() => {
        if (configuration) {
            if (availableGame) {
                setCurrentStep((availableGame.lastStep ?? 0) + 1);
                if (availableGame.lastStep === currentStep) {
                    setLoading(false);
                }
                setStepContent(configuration[(availableGame.lastStep ?? 0) + 1]);
            } else {
                setStepContent(configuration[currentStep]);
            }
        }
    }, [configuration, currentStep, availableGame]);

    useEffect(() => {
        setCurrentStepStats(DEFAULT_STATS);
    }, [currentStep]);

    const onConfirm = () => {
        setStepContent({ id: 100 });
        setLoading(true);
        if (currentStep > 1) {
            saveProgress(currentStep, currentStepStats, availableGame?.token ?? null);
            nextStepAction({});
        }
        if (currentStep === 1) {
            nextStepAction({ availableGame, startGame });
        }
        if (currentStep === lastStep) {
            saveProgress(currentStep, currentStepStats, availableGame?.token ?? null);
            nextStepAction({ availableGame, endGame });
        }
    };

    if (currentStep === 35 || availableGame?.finalTime !== null) {
        return (
            <GameWrapper>
                <MEnding title={title} currentStep={currentStep} />
            </GameWrapper>
        );
    }

    if (isEmptyObject(stepContent) && currentStep < 35) {
        return (
            <>
                <GameWrapper>
                    <div className="card game-card">
                        <Loading color="#6f32a8" />
                    </div>
                </GameWrapper>
            </>
        );
    }

    if (availableGame && currentStep < 35) {
        const lastStep = availableGame.lastStep;
        if (lastStep === null || (lastStep && currentStep < lastStep)) {
            return (
                <>
                    <GameWrapper>
                        <div className="card game-card">
                            <Loading color="#6f32a8" />
                        </div>
                    </GameWrapper>
                </>
            );
        }
    }

    if (loading && currentStep < 35) {
        return (
            <>
                <GameWrapper>
                    <div className="card game-card">
                        <Loading color="#6f32a8" />
                    </div>
                </GameWrapper>
            </>
        );
    }

    return (
        <>
            <GameWrapper>
                {currentStep !== 35 && (
                    <div className="card game-card m-game-card">
                        {title && <MTaskTitle text={title} />}
                        {taskText && <MTaskText text={taskText} title={title} />}
                        <MCipher onClickNext={onConfirm} />
                        {codeInputText && (
                            <MCodeInput
                                text={codeInputText}
                                passCodeRegexp={codeInputPassCodeRegexp}
                                onConfirm={onConfirm}
                                isNumber={mGameNumberSteps.includes(currentStep)}
                            />
                        )}
                        {diaryIcon && <MDiaryIcon />}
                        {continueButtonText && <MContinueButton text={continueButtonText} onClick={onConfirm} />}
                    </div>
                )}
            </GameWrapper>
        </>
    );
}

export default MGame;
